import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";

import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";

import { HistoryRecord, PlanRecord } from "@shared/models";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { convertToHHMM, convertToMinutes, formatTimeInput } from "@shared/functions";
import { GlobalService } from "@shared/services";
import { ButtonComponent } from "@shared/components/button/button.component";

@Component({
  selector: "app-edit-dialog",
  standalone: true,
  imports: [DialogModule, ButtonModule, FormsModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: "./edit-dialog.component.html",
  styleUrl: "./edit-dialog.component.scss",
})
export class EditDialogComponent implements OnInit {
  globalService = inject(GlobalService);
  @Input() visible = false;
  @Input() data: HistoryRecord | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onHistorySave = new EventEmitter<HistoryRecord>();
  @Output() onRecordRefresh = new EventEmitter<number>();

  header: string = "";

  editHistoryInputs = {
    timeInputs: [
      { label: "VMAX", formControlName: "vmax" },
      { label: "PROJ", formControlName: "proj" },
      { label: "IMAX", formControlName: "imax" },
    ],
    dayInputs: [
      { label: "Days Worked", formControlName: "daysWorked" },
      { label: "PVDs", formControlName: "usedPVDs" },
      { label: "Sick", formControlName: "usedSick" },
      { label: "Training", formControlName: "usedTraining" },
    ],
  };

  editHistoryForm = new FormGroup({
    vmax: new FormControl(""),
    proj: new FormControl(""),
    imax: new FormControl(""),
    daysWorked: new FormControl(0),
    usedPVDs: new FormControl(0),
    usedSick: new FormControl(0),
    usedTraining: new FormControl(0),
  });

  ngOnInit(): void {
    if (this.isHistoryRecord(this.data)) {
      this.header = "Edit History";
      this.editHistoryForm.setValue({
        vmax: convertToHHMM({ minutes: this.data.vmaxMinutes }),
        proj: convertToHHMM({ minutes: this.data.projMinutes }),
        imax: convertToHHMM({ minutes: this.data.imaxMinutes }),
        daysWorked: this.data.daysWorked,
        usedPVDs: this.data.usedPVDs,
        usedSick: this.data.usedSick,
        usedTraining: this.data.usedTraining,
      });
    }
  }

  onKeyDown(keystroke: KeyboardEvent, formControlName: string) {
    const newValue = formatTimeInput(keystroke);
    this.editHistoryForm.get(formControlName)?.setValue(newValue);
  }

  refreshRecord(record: HistoryRecord | PlanRecord | null): void {
    if (this.isHistoryRecord(record)) {
      this.onRecordRefresh.emit(record.historyID);
      this.close();
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  save(): void {
    if (this.isHistoryRecord(this.data)) {
      const formValue = this.editHistoryForm.value;
      const updatedData = {
        ...this.data,
        vmaxMinutes: convertToMinutes(formValue.vmax || ""),
        projMinutes: convertToMinutes(formValue.proj || ""),
        imaxMinutes: convertToMinutes(formValue.imax || ""),
        daysWorked: formValue.daysWorked || 0,
        usedPVDs: formValue.usedPVDs || 0,
        usedSick: formValue.usedSick || 0,
        usedTraining: formValue.usedTraining || 0,
      };

      this.onHistorySave.emit(updatedData);
      this.close();
    }
  }

  isHistoryRecord(data: HistoryRecord | PlanRecord | null): data is HistoryRecord {
    return !!data && "historyID" in data;
  }
}
